// components/ProcessSection.js
import React, { useEffect, useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import upload from '../../assets/images/Upload.png';
import mani1 from '../../assets/images/Main.png';
import home1 from '../../assets/images/Home.png';

// Enhanced ProcessStep with LazyLoadImage
const ProcessStep = ({ image, title, description, index, isVisible, isActive }) => {
  return (
    <div 
      className={`flex flex-col items-center text-center max-w-sm transform transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 200}ms` }}
    >
      {/* Step Number Badge - All start as neutral, change on hover */}
      <div className={`absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full font-bold flex items-center justify-center shadow-lg z-10 transition-all duration-300 
        bg-white text-gray-800 border-2 border-gray-200 
        group-hover:bg-red-600 group-hover:text-white group-hover:scale-110 group-hover:border-white
      `}>
        <span className="text-lg">{index + 1}</span>
      </div>
      
      {/* Image Container - All start as neutral, change on hover */}
      <div className="relative flex items-center justify-center h-72 mb-8 p-6 rounded-xl transition-all duration-500 overflow-hidden group 
        shadow-lg bg-white hover:shadow-2xl hover:border-l-4 hover:border-red-600 hover:-translate-y-2
      ">
        {/* Background Pattern with animation */}
        <div className="absolute inset-0 transition-all duration-500 opacity-5 group-hover:opacity-10">
          <div className="absolute inset-0 bg-dot-pattern"></div>
        </div>
        
        {/* Glow effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-orange-500/0 group-hover:from-red-500/5 group-hover:to-orange-500/10 transition-all duration-500"></div>
        
        {/* Replace standard img with LazyLoadImage */}
        <LazyLoadImage
          src={image}
          alt={title}
          effect="blur"
          wrapperClassName="w-full h-full relative z-10"
          className="w-full h-full object-contain transform transition-all duration-500 
            scale-100 group-hover:scale-110 filter group-hover:drop-shadow-xl"
          placeholderSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f5f5f5'/%3E%3C/svg%3E"
        />
        
        {/* Hover Border */}
        <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-red-500/30 transition-all duration-300 z-20"></div>
        
        {/* Shine effect on hover */}
        <div className="absolute inset-0 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-20">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1500"></div>
        </div>
      </div>
      
      {/* Content - All start as neutral, change on hover */}
      <h3 className="text-2xl font-bold mb-4 transition-all duration-500 relative text-gray-800 group-hover:text-red-600">
        {/* Underline animation on hover */}
        <span className="relative inline-block">
          {title}
          <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-500 group-hover:w-full transition-all duration-300"></span>
        </span>
      </h3>
      <p className="text-gray-600 leading-relaxed max-w-xs mx-auto group-hover:text-gray-700 transition-colors duration-300">
        {description}
      </p>
    </div>
  );
};

const ProcessSection = ({ onTryNow }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [lineProgress, setLineProgress] = useState(0);
  const sectionRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const sectionTop = rect.top;
      const windowHeight = window.innerHeight;
      
      const scrollStart = windowHeight * 0.75;
      const scrollEnd = windowHeight * 0.25;
      
      if (sectionTop <= scrollStart && sectionTop >= scrollEnd) {
        const progress = (scrollStart - sectionTop) / (scrollStart - scrollEnd);
        setLineProgress(progress);
        
        if (progress < 0.33) {
          setActiveStep(0);
        } else if (progress < 0.66) {
          setActiveStep(1);
        } else {
          setActiveStep(2);
        }
      } else if (sectionTop < scrollEnd) {
        setLineProgress(1);
        setActiveStep(2);
      } else {
        setLineProgress(0);
        setActiveStep(0);
      }
    };

    const onScroll = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(handleScroll);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const steps = [
    {
      image: upload,
      title: "Upload your parts online",
      description: "Upload your OBJ Format to get the best quotation from 195+ vendors within 1 minute."
    },
    {
      image: mani1,
      title: "We start manufacturing",
      description: "Depending upon the order type, we start your manufacturing in our facilities as fast as 20 minutes."
    },
    {
      image: home1,
      title: "Timely door-step delivery",
      description: "We take care of all the operations and logistics hassle, making sure that your parts are delivered before time."
    }
  ];

  const handleUploadClick = () => {
    const email = 'prathameshlabde2278@gmail.com';
    const subject = encodeURIComponent('3D Print Request - File Upload');
    const body = encodeURIComponent(`Dear 3D PrintTech Team,

I would like to request a quote for my 3D printing project. Please find my file attached.

Project Details:
- File Format: [STL/OBJ]
- Quantity: [Please specify]
- Material Preference: [Please specify]
- Color: [Please specify]
- Additional Requirements: [Please specify]

Please provide me with a quotation and estimated delivery time.

Thank you!

Best regards,
[Your Name]
[Your Contact Information]`);
    
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  return (
    <div ref={sectionRef} className="my-20 relative">
      {/* Enhanced Background with subtle animation */}
      <div className="absolute inset-0 bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-500">
        {/* Enhanced Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0 grid-pattern"></div>
        </div>
      </div>
      
      <div className="relative z-10 py-16 px-4">
        {/* Enhanced Header with animation */}
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 transition-transform duration-500 hover:scale-105">
            Our Printing Process
          </h2>
          <div className="h-1 w-24 mx-auto bg-red-600 rounded-full transition-all duration-500 hover:w-32"></div>
          <p className="text-gray-600 max-w-2xl mx-auto mt-6 text-lg">
            Experience a seamless 3D printing journey from design to delivery with our streamlined three-step process
          </p>
        </div>
        
        <div className="relative">
          {/* Enhanced Connection Line - Only on Desktop */}
          <div className="hidden md:block absolute top-36 left-0 right-0 h-1">
            {/* Background Line */}
            <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
            
            {/* Animated Progress Line with pulse effect */}
            <div 
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-200 ease-out"
              style={{ width: `${lineProgress * 100}%` }}
            ></div>
            
            {/* Enhanced Progress Dots with consistent hover states for all dots */}
            {[0, 1, 2].map((i) => (
              <div 
                key={i}
                className="absolute top-1/2 transform -translate-y-1/2 transition-all duration-500"
                style={{ left: `${i * 33.33 + 16.67}%` }}
              >
                <div 
                  className={`w-6 h-6 rounded-full border-2 transition-all duration-300 hover:scale-125 hover:border-red-300 hover:bg-red-50 cursor-pointer ${
                    i <= activeStep 
                      ? 'bg-red-600 border-white shadow-md hover:bg-red-500' 
                      : 'bg-white border-gray-300'
                  }`}
                ></div>
              </div>
            ))}
          </div>
          
          {/* Process Steps - Each step now has its own hover state */}
          <div className="flex flex-col md:flex-row items-stretch justify-between space-y-12 md:space-y-0 md:space-x-8 px-4 md:px-12">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="flex-1 group relative cursor-pointer hover:z-10" 
              >
                <ProcessStep 
                  {...step}
                  index={index}
                  isVisible={isVisible}
                  isActive={index === activeStep}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Enhanced CTA Section */}
        <div className="mt-20 text-center">
          <div className={`transform transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <button 
              className="group relative px-10 py-4 bg-red-600 text-white font-bold text-lg rounded-xl shadow-lg transition-all duration-300 flex items-center gap-3 mx-auto hover:bg-red-700 hover:shadow-xl active:scale-95 overflow-hidden"
              onClick={handleUploadClick}
            >
              {/* Enhanced Shine Effect */}
              <div className="absolute inset-0 rounded-xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
              
              {/* Button Icon with animation */}
              <svg 
                className="w-6 h-6 transition-transform duration-300 relative z-10 group-hover:rotate-12" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              
              {/* Button text with subtle animation */}
              <span className="relative z-10 group-hover:tracking-wider transition-all duration-300">Upload Your File</span>
              
              {/* Subtle background pulse effect */}
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 rounded-xl transition-all duration-300"></div>
            </button>
            
            <p className="mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors duration-300">
              Have a file ready? Upload it now and get an instant quote
            </p>
          </div>
        </div>
      </div>
      
      {/* CSS for Enhanced Grid Pattern */}
      <style jsx>{`
        .grid-pattern {
          background-image: 
            linear-gradient(to right, #000 1px, transparent 1px),
            linear-gradient(to bottom, #000 1px, transparent 1px);
          background-size: 40px 40px;
        }
        
        .bg-dot-pattern {
          background-image: radial-gradient(rgba(0, 0, 0, 0.2) 1px, transparent 1px);
          background-size: 20px 20px;
        }
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
          100% { transform: translateY(0px); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ProcessSection;